import { Component, EventEmitter, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { GlobalService } from 'src/app/app-core/lib/global.service';
import { ProcessService } from 'src/app/app-core/services/process.service';
import { DocumentSignatureService } from 'src/app/app-core/services/document-signature.service';
import { environment } from 'src/environments/environment';
import { SignedFileDataResponse } from 'src/app/interfaces/signature-gov.interfaces';

@Component({
    selector: 'app-create-parecer-dispatch-dialog',
    templateUrl: './create-parecer-dispatch-dialog.component.html',
    styleUrls: ['./create-parecer-dispatch-dialog.component.scss'],
    providers: [MessageService, ProcessService, DocumentSignatureService],
})
export class CreateParecerDispatchDialogComponent implements OnChanges, OnDestroy {
    @Input() process: any;
    @Input() isModalVisible: boolean = false;
    @Input() existingDraft: any = null;

    @Output() closed = new EventEmitter<void>();
    @Output() saved = new EventEmitter<any>();

    public parecerId: number | null = null;
    public numeroParecer: string = '';
    public ementa: string = '';
    public content: string = '';
    public secretarySignatureRequired: boolean = false;
    public isSaving: boolean = false;
    public isSigning: boolean = false;

    public availableSecretaries: string[] = [];
    public selectedSecretary: string | null = null;
    public isLoadingSecretaries: boolean = false;
    public isPreviewing: boolean = false;
    public signingMessage: string = '';

    public editorConfig = {
        height: 480,
        base_url: '/tinymce',
        suffix: '.min',
        menubar: false,
        statusbar: false,
        toolbar_mode: 'wrap',
        plugins:
            'preview importcss searchreplace autolink autosave save directionality visualblocks visualchars fullscreen image link media template codesample table charmap pagebreak nonbreaking anchor insertdatetime advlist lists wordcount help charmap quickbars emoticons',
        toolbar:
            'undo redo | blocks | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | outdent indent | table | numlist bullist checklist | forecolor backcolor removeformat | link image media codesample | pagebreak charmap emoticons | fullscreen preview',
        table_toolbar:
            'tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol | tablemergecells tablesplitcells',
        table_default_attributes: { border: '1' },
        table_default_styles: { 'border-collapse': 'collapse', width: '100%' },
        fontsize_formats: '8pt 10pt 12pt 14pt 18pt 24pt 36pt',
        wordcount_display: false,
        language_url: '/assets/languages/pt_BR.js',
        language: 'pt_BR',
        content_style: 'body { font-family:Inter, sans-serif; font-size:14px; } table { border-collapse: collapse; } table td, table th { border: 1px solid #999; padding: 4px 8px; }',
        setup: (editor: any) => {
            const getBlock = (node: any) => editor.dom.getParent(node, 'p,div,h1,h2,h3,h4,h5,h6');

            editor.on('keydown', (event: KeyboardEvent) => {
                if (event.key === 'Tab') {
                    const node = editor.selection.getNode();
                    const block = getBlock(node);

                    if (!block || editor.dom.getParent(node, 'li,td,th')) return;

                    event.preventDefault();
                    editor.dom.setStyle(block, 'text-indent', event.shiftKey ? '' : '4em');
                    return;
                }

                
                if (event.key === 'Enter' && event.shiftKey) {
                    const before = getBlock(editor.selection.getNode());
                    setTimeout(() => {
                        const after = getBlock(editor.selection.getNode());
                        if (after && after !== before) {
                            editor.dom.setStyle(after, 'text-indent', '');
                        }
                    });
                }
            });
        },
    };

    private destroy$ = new Subject<void>();
    private popupPollInterval: any = null;
    private popupTimeoutHandle: any = null;
    private pendingSignContext: {
        pdfBase64: string;
        fileName: string;
        pageCount: number;
        signaturePosition: { page: number; xPercentage: number; yPercentage: number } | null;
        secretarioSignaturePosition: { page: number; xPercentage: number; yPercentage: number } | null;
    } | null = null;
    private secretarioSignaturePosition: { page: number; xPercentage: number; yPercentage: number } | null = null;
    private boundMessageListener: ((event: MessageEvent) => void) | null = null;

    constructor(
        private global: GlobalService,
        private processService: ProcessService,
        private documentSignatureService: DocumentSignatureService,
        private ngZone: NgZone,
        private messageService: MessageService,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.isModalVisible && this.isModalVisible) {
            this.hydrateFromDraft();
            if (this.secretarySignatureRequired) {
                this.loadSecretaries();
            }
        }
    }

    onSecretaryCheckboxChange(): void {
        if (this.secretarySignatureRequired) {
            this.loadSecretaries();
        } else {
            this.availableSecretaries = [];
            this.selectedSecretary = null;
        }
    }

    private loadSecretaries(): void {
        if (!this.process?.PRC_CodigoProcesso) return;
        this.isLoadingSecretaries = true;
        this.processService
            .getParecerSecretarios(this.process.PRC_CodigoProcesso)
            .pipe(takeUntil(this.destroy$), finalize(() => (this.isLoadingSecretaries = false)))
            .subscribe({
                next: ({ secretarios }) => {
                    this.availableSecretaries = secretarios || [];
                    if (this.availableSecretaries.length === 1) {
                        this.selectedSecretary = this.availableSecretaries[0];
                    } else if (this.selectedSecretary && !this.availableSecretaries.includes(this.selectedSecretary)) {
                        this.selectedSecretary = null;
                    }
                },
                error: () => {
                    this.availableSecretaries = [];
                    this.selectedSecretary = null;
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Atenção',
                        detail: 'Não foi possível carregar os secretários da secretaria.',
                    });
                },
            });
    }

    get needsSecretarySelection(): boolean {
        return this.secretarySignatureRequired && this.availableSecretaries.length > 1;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.clearPopupPoll();
    }

    get interessado(): string {
        return this.process?.SOLR_NomeRazaoSocial || this.process?.PE_Nome || '';
    }

    get assunto(): string {
        return this.process?.AS_Descricao || '';
    }

    get processNumber(): string {
        return this.process?.PRC_NumeroProcesso || '';
    }

    get isReadOnly(): boolean {
        return !!this.existingDraft?.PPD_Assinado;
    }

    onModalHide(): void {
        this.resetState();
        this.closed.emit();
    }

    onSaveDraftClick(): void {
        if (!this.validateForm()) return;
        this.persistDraft().subscribe({
            next: (parecer) => {
                this.parecerId = parecer.PPD_CodigoParecerDespacho;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Rascunho salvo',
                    detail: 'O parecer foi salvo como rascunho.',
                });
                this.saved.emit(parecer);
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Não foi possível salvar o rascunho.',
                });
            },
        });
    }

    onSignClick(): void {
        if (!this.validateForm()) return;
        this.isSigning = true;
        this.signingMessage = 'Salvando rascunho...';
        this.persistDraft().subscribe({
            next: (parecer) => {
                this.parecerId = parecer.PPD_CodigoParecerDespacho;
                this.signingMessage = 'Gerando PDF para assinatura...';
                this.preparePdfAndOpenGovBr();
            },
            error: () => {
                this.endSigningFlow();
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Não foi possível preparar o parecer para assinatura.',
                });
            },
        });
    }

    onPreviewClick(): void {
        if (!this.validateForm()) return;
        this.isPreviewing = true;
        const user = this.global.user();
        this.processService
            .previewParecer(this.process.PRC_CodigoProcesso, {
                numeroParecer: this.numeroParecer,
                ementa: this.ementa,
                conteudoHtml: this.content,
                assinaturaSecretarioObrigatoria: this.secretarySignatureRequired,
                nomeSecretario: this.secretarySignatureRequired ? this.selectedSecretary : null,
                userId: user?.USR_CodigoUsuario ?? user?.PE_CodigoPessoa,
            })
            .pipe(takeUntil(this.destroy$), finalize(() => (this.isPreviewing = false)))
            .subscribe({
                next: (blob) => {
                    const url = URL.createObjectURL(blob);
                    window.open(url, '_blank');
                    setTimeout(() => URL.revokeObjectURL(url), 60_000);
                },
                error: () => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Não foi possível gerar a pré-visualização.',
                    });
                },
            });
    }

    private endSigningFlow(): void {
        this.isSigning = false;
        this.signingMessage = '';
    }

    private validateForm(): boolean {
        if (!this.content?.trim()) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Escreva o conteúdo do parecer.',
            });
            return false;
        }
        if (this.needsSecretarySelection && !this.selectedSecretary) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Selecione o(a) secretário(a) responsável pela assinatura.',
            });
            return false;
        }
        return true;
    }

    private persistDraft() {
        this.isSaving = true;
        const userId = this.global.user()?.PE_CodigoPessoa;

        const payload = {
            numeroParecer: this.numeroParecer,
            ementa: this.ementa,
            conteudoHtml: this.content,
            assinaturaSecretarioObrigatoria: this.secretarySignatureRequired,
            nomeSecretario: this.secretarySignatureRequired ? this.selectedSecretary : null,
        };

        const request$ = this.parecerId
            ? this.processService.updateParecerDraft(this.process.PRC_CodigoProcesso, this.parecerId, payload)
            : this.processService.createParecerDraft(this.process.PRC_CodigoProcesso, { ...payload, userId });

        return request$.pipe(
            takeUntil(this.destroy$),
            finalize(() => (this.isSaving = false)),
        );
    }

    private preparePdfAndOpenGovBr(): void {
        if (!this.parecerId) {
            this.endSigningFlow();
            return;
        }
        this.processService
            .prepareParecerSign(this.process.PRC_CodigoProcesso, this.parecerId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: any) => {
                    const { pdfBase64, fileName, pageCount, signaturePosition, secretarioSignaturePosition } = response;
                    this.secretarioSignaturePosition = secretarioSignaturePosition || null;
                    this.signingMessage = 'Aguardando assinatura no gov.br...';
                    this.openGovBrPopup(pdfBase64, fileName, pageCount, signaturePosition, secretarioSignaturePosition);
                },
                error: () => {
                    this.endSigningFlow();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Falha ao gerar PDF para assinatura.',
                    });
                },
            });
    }

    private openGovBrPopup(
        pdfBase64: string,
        fileName: string,
        pageCount: number,
        signaturePosition: { page: number; xPercentage: number; yPercentage: number } | null,
        secretarioSignaturePosition: { page: number; xPercentage: number; yPercentage: number } | null,
    ): void {
        const signUrl = `${environment.govBR.signUrl}/authorize?response_type=code&redirect_uri=${encodeURIComponent(
            environment.govBR.returnSignUrl,
        )}&scope=sign&client_id=${environment.govBR.signClientID}`;
        const width = 800;
        const height = 600;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;

        const popup = window.open(signUrl, '_blank', `width=${width},height=${height},top=${top},left=${left}`);

        this.pendingSignContext = {
            pdfBase64,
            fileName,
            pageCount,
            signaturePosition,
            secretarioSignaturePosition,
        };
        this.registerPostMessageFallback();

        const POLL_TIMEOUT_MS = 5 * 60 * 1000;
        const startedAt = Date.now();

        this.popupPollInterval = setInterval(() => {
            if (!popup || popup.closed) {
                this.clearPopupPoll();
                this.ngZone.run(() => {
                    if (this.pendingSignContext) {
                        this.pendingSignContext = null;
                        this.endSigningFlow();
                        this.messageService.add({
                            severity: 'warn',
                            summary: 'Assinatura cancelada',
                            detail: 'A janela de assinatura foi fechada antes da conclusão.',
                        });
                    }
                });
                return;
            }

            if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
                try { popup.close(); } catch { /* noop */ }
                this.clearPopupPoll();
                this.ngZone.run(() => {
                    this.pendingSignContext = null;
                    this.endSigningFlow();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Tempo esgotado',
                        detail: 'A assinatura não foi concluída a tempo. Tente novamente.',
                    });
                });
                return;
            }

            try {
                if (popup.location.href.includes('code=')) {
                    const url = new URL(popup.location.href);
                    const code = url.searchParams.get('code');
                    popup.close();
                    this.clearPopupPoll();
                    this.ngZone.run(() => {
                        if (code && this.pendingSignContext) {
                            const ctx = this.pendingSignContext;
                            this.pendingSignContext = null;
                            this.callSignApi(code, ctx.pdfBase64, ctx.fileName, ctx.pageCount, ctx.signaturePosition);
                        } else {
                            this.endSigningFlow();
                        }
                    });
                }
            } catch {
                // Cross-origin while popup is on gov.br domain — wait until redirect to same-origin app
            }
        }, 1000);
    }

    private registerPostMessageFallback(): void {
        if (this.boundMessageListener) return;

        this.boundMessageListener = (event: MessageEvent) => {
            try {
                const returnUrlOrigin = new URL(environment.govBR.returnSignUrl).origin;
                if (event.origin !== returnUrlOrigin) return;
                const code = (event.data && (event.data.code || event.data.signCode)) as string | undefined;
                if (!code || !this.pendingSignContext) return;
                const ctx = this.pendingSignContext;
                this.pendingSignContext = null;
                this.clearPopupPoll();
                this.ngZone.run(() => this.callSignApi(code, ctx.pdfBase64, ctx.fileName, ctx.pageCount, ctx.signaturePosition));
            } catch {
                /* noop */
            }
        };
        window.addEventListener('message', this.boundMessageListener);
    }

    private callSignApi(
        code: string,
        pdfBase64: string,
        fileName: string,
        pageCount: number,
        computedPosition: { page: number; xPercentage: number; yPercentage: number } | null,
    ): void {
        const lastPageIndex = Math.max(0, (pageCount || 1) - 1);
        const signaturePosition = computedPosition || { page: lastPageIndex, xPercentage: 50, yPercentage: 15 };
        this.signingMessage = 'Assinando documento...';
        this.documentSignatureService
            .signatureAndSaveDocument({
                code,
                fileName,
                pdfBase64,
                signaturePosition,
            })
            .subscribe({
                next: (response: SignedFileDataResponse) => {
                    this.signingMessage = 'Finalizando assinatura...';
                    this.finalizeSign(response.fileCloudFlare, fileName);
                },
                error: () => {
                    this.endSigningFlow();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Falha na assinatura via gov.br.',
                    });
                },
            });
    }

    private finalizeSign(fileCloudFlare: string, fileName: string): void {
        if (!this.parecerId) return;
        this.processService
            .finalizeParecerSign(this.process.PRC_CodigoProcesso, this.parecerId, {
                fileCloudFlare,
                fileName: `${fileName}.pdf`,
                secretarioSignaturePosition: this.secretarioSignaturePosition,
            })
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.endSigningFlow()),
            )
            .subscribe({
                next: (parecer) => {
                    const signedParecer = { ...parecer, __signedJustNow: true };
                    this.resetState();
                    this.closed.emit();
                    setTimeout(() => this.saved.emit(signedParecer));
                },
                error: () => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Não foi possível finalizar a assinatura.',
                    });
                },
            });
    }

    private hydrateFromDraft(): void {
        if (this.existingDraft) {
            this.parecerId = this.existingDraft.PPD_CodigoParecerDespacho;
            this.numeroParecer = this.existingDraft.PPD_NumeroParecer || '';
            this.ementa = this.existingDraft.PPD_Ementa || '';
            this.content = this.existingDraft.PPD_ConteudoHTML || '';
            this.secretarySignatureRequired = !!this.existingDraft.PPD_AssinaturaSecretarioObrigatoria;
            this.selectedSecretary = this.existingDraft.PPD_NomeSecretario || null;
        } else {
            this.parecerId = null;
            this.numeroParecer = '';
            this.ementa = '';
            this.content = '';
            this.secretarySignatureRequired = false;
            this.selectedSecretary = null;
        }
        this.availableSecretaries = [];
    }

    private resetState(): void {
        this.parecerId = null;
        this.numeroParecer = '';
        this.ementa = '';
        this.content = '';
        this.secretarySignatureRequired = false;
        this.selectedSecretary = null;
        this.availableSecretaries = [];
        this.isSaving = false;
        this.isSigning = false;
        this.clearPopupPoll();
    }

    private clearPopupPoll(): void {
        if (this.popupPollInterval) {
            clearInterval(this.popupPollInterval);
            this.popupPollInterval = null;
        }
        if (this.popupTimeoutHandle) {
            clearTimeout(this.popupTimeoutHandle);
            this.popupTimeoutHandle = null;
        }
        if (this.boundMessageListener) {
            window.removeEventListener('message', this.boundMessageListener);
            this.boundMessageListener = null;
        }
    }
}
